import {mapObject} from '@benchling/js-utils';

import {MigrationObjectChange} from 'config-migration/import/import-changelog/MigrationChangelogConstants';
import {
  MigrationError,
  MigrationObjectAttributes,
  MigrationStatus,
} from 'config-migration/MigrationConstants';
import * as ImportUtil from 'util/ImportUtil';
import * as TaskRunner from 'util/TaskRunner';

import {transformObjectActionToServerRepresentation} from './import/planning-logic/transformPlannedActions';
import {LogMessageData} from './migration-log/Log';
import {DependencySummary} from './types/DependencySummary';
import {DestinationObjectResponse} from './types/DestinationObjectsResponse';
import {ListModelsSummary} from './types/ListModelsResponse';
import {DestinationObject, ImportObject} from './types/MigrationObjects';
import {ActionsByObjectId} from './types/objectActions/ObjectAction';
import {PrimaryObjectType, SecondaryObjectType} from './types/ObjectType';

// BNCH-70874: We are currently running an epic to improve performance, but in the interim, we want to ensure
//  that long running operations complete. The TaskRunner by default times out after ~11 minutes.
//  Because our operations can currently take up to 30 minutes (given our elevated Celery timeouts), we need
//    to override the defaults.
// TODO(BNCH-68125): Lower/remove this elevated timeout when we have our celery timeouts reduced.
const CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS: number = 1800000; // 30 minutes

export default {
  listConfigMigrationModels,
  exportConfigMigration,
  planConfigMigration,
  exportConfigMigrationDiff,
  validateConfigMigration,
  importConfigMigration,
  getConfigMigrationURL,
  uploadMigrationFile,
};

export interface ListConfigMigrationModelsResponse {
  dependencySummary: ListModelsSummary;
}

function listConfigMigrationModels(organizationId: string): Promise<ListConfigMigrationModelsResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/config-migration:list-models',
    data: {
      organizationId,
    },
  });
}

// Keep in sync with export task response in benchling/config_migration/response_objects/export/export_response.py
export interface ExportResponse {
  exportUrl: string;
  dependencies: Array<LogMessageData>;
  dependencySummary: DependencySummary;
  errors: Array<LogMessageData>;
  errorLogFilename: string;
  dependencyLogFilename: string;
  hasExternalFiles: boolean;
}

function exportConfigMigration(
  organizationId: string,
  apiIdsToExport: Array<string> | null
): Promise<ExportResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:export',
      data: {
        organizationId,
        apiIdsToExport,
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

export interface PlanConfigMigrationResponse {
  importObjects: Array<ImportObject>;
  destinationObjects: DestinationObjectResponse;
  importDisabledObjects: Array<ImportDisabledObject>;
}

export interface ImportDisabledObject {
  displayName: string;
  type: PrimaryObjectType | SecondaryObjectType;
}

export interface ImportUnplannableObject extends ImportDisabledObject {
  provenanceUri: string;
}

// Keep in sync with benchling/config_migration/response_objects/plan/plan_config_migration_response.py
interface PlanConfigMigrationServerResponse {
  importObjects: Array<ServerImportObjectResponse>;
  destinationObjects: Array<ServerDestinationObject>;
  importDisabledObjects: Array<ServerImportDisabledObject>;
}

interface ServerImportObjectResponse {
  objectType: PrimaryObjectType | SecondaryObjectType;
  objectAttributes: MigrationObjectAttributes;
  identifier: ServerImportObjectIdentifier;
  children: Array<ServerImportObjectResponse>;
  unplannableChildObjects: Array<ServerImportUnplannableObject>;
}

interface ServerPartialObjectIdentifier {
  apiIdentifier: string;
  displayName: string;
}

interface ServerImportObjectIdentifier extends ServerPartialObjectIdentifier {
  provenanceUri: string;
}

interface ServerDestinationObject {
  objectType: PrimaryObjectType | SecondaryObjectType;
  objectAttributes: MigrationObjectAttributes;
  identifier: ServerDestinationObjectIdentifier;
  children: Array<ServerDestinationObject>;
  matchResult: ServerMatchResult | null;
  objectInaccessible: boolean;
}

interface ServerDestinationObjectIdentifier extends ServerPartialObjectIdentifier {
  provenanceUri: string;
  archived: boolean;
  parentIdentifier: ServerDestinationObjectIdentifier | null;
}

interface ServerMatchResult {
  sourceApiIdentifier: string | null;
  imported: boolean;
  equivalent: boolean;
}

interface ServerImportDisabledObject {
  identifier: ServerPartialObjectIdentifier;
  objectType: PrimaryObjectType;
  reason: ImportDisabledReason;
  requiredConfigFlags: Array<Map<string, boolean>>;
}

enum ImportDisabledReason {
  ALWAYS_DISABLED = 'ALWAYS_DISABLED',
  INTERNAL_ADMIN_ONLY = 'INTERNAL_ADMIN_ONLY',
  CONFIG_FLAG_MISMATCH = 'CONFIG_FLAG_MISMATCH',
}

interface ServerImportUnplannableObject {
  identifier: ServerImportObjectIdentifier;
  objectType: PrimaryObjectType;
}

function planConfigMigration(
  organizationId: string,
  importUrl: string
): Promise<PlanConfigMigrationResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:plan',
      data: {
        organizationId,
        importUrl,
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  ).then((response: PlanConfigMigrationServerResponse) => {
    const destinationUnmatchedObjects: Array<DestinationObject> = [];
    const destinationMatchedDiffingObjects: Array<DestinationObject> = [];
    const destinationMatchedEquivalentObjects: Array<DestinationObject> = [];
    const destinationMatchedUnexportedObjects: Array<DestinationObject> = [];
    const importIdenticalApiIdentifiers = new Set<string>();
    response.destinationObjects.forEach((destObj) => {
      const converted: DestinationObject = transformServerDestinationObject(destObj);
      if (destObj.matchResult == null) {
        destinationUnmatchedObjects.push(converted);
      } else if (destObj.matchResult.imported) {
        if (destObj.matchResult.equivalent) {
          importIdenticalApiIdentifiers.add(destObj.matchResult.sourceApiIdentifier!);
          destinationMatchedEquivalentObjects.push(converted);
        } else {
          destinationMatchedDiffingObjects.push(converted);
        }
      } else {
        destinationMatchedUnexportedObjects.push(converted);
      }
    });
    const importOnlyObjects = response.importObjects
      .filter((importObj) => !importIdenticalApiIdentifiers.has(importObj.identifier.apiIdentifier))
      .map((importObj) => transformServerImportObject(importObj));

    const importDisabledObjects: Array<ImportDisabledObject> = response.importDisabledObjects.map((obj) =>
      transformServerImportDisabledObject(obj)
    );
    return {
      importObjects: importOnlyObjects,
      destinationObjects: {
        notProvenanceIdMatched: destinationUnmatchedObjects,
        provenanceIdMatched: {
          notSourceExported: destinationMatchedUnexportedObjects,
          sourceExported: {
            equivalent: destinationMatchedEquivalentObjects,
            notEquivalent: destinationMatchedDiffingObjects,
          },
        },
      },
      importDisabledObjects,
    };
  });
}

function transformServerImportDisabledObject(
  serverResponse: ServerImportDisabledObject
): ImportDisabledObject {
  return {
    displayName: serverResponse.identifier.displayName,
    type: serverResponse.objectType,
  };
}

function transformServerImportUnplannableObject(
  serverResponse: ServerImportUnplannableObject
): ImportUnplannableObject {
  return {
    displayName: serverResponse.identifier.displayName,
    type: serverResponse.objectType,
    provenanceUri: serverResponse.identifier.provenanceUri,
  };
}

function transformServerDestinationObject(destinationResponse: ServerDestinationObject): DestinationObject {
  return {
    objectType: destinationResponse.objectType,
    objectAttributes: destinationResponse.objectAttributes,
    identifier: {
      apiIdentifier: destinationResponse.identifier.apiIdentifier,
      provenanceUri: destinationResponse.identifier.provenanceUri,
      displayName: destinationResponse.identifier.displayName,
      archived: destinationResponse.identifier.archived,
      parentIdentifier: destinationResponse.identifier.parentIdentifier,
      objectInaccessible: destinationResponse.objectInaccessible,
    },
    childObjects: destinationResponse.children.map((child) => transformServerDestinationObject(child)),
  };
}

function transformServerImportObject(importResponse: ServerImportObjectResponse): ImportObject {
  return {
    objectType: importResponse.objectType,
    objectAttributes: importResponse.objectAttributes,
    identifier: {
      provenanceUri: importResponse.identifier.provenanceUri,
      displayName: importResponse.identifier.displayName,
    },
    childObjects: importResponse.children.map((child) => transformServerImportObject(child)),
    unplannableChildObjects: importResponse.unplannableChildObjects.map((child) =>
      transformServerImportUnplannableObject(child)
    ),
  };
}

// Keep in sync with benchling/config_migration/response_objects/diff/generate_diff_response.py
interface ConfigMigrationDiffResponse {
  url: string;
}

function exportConfigMigrationDiff(
  organizationId: string,
  importUrl: string
): Promise<ConfigMigrationDiffResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:diff',
      data: {
        organizationId,
        importUrl,
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

// Keep in sync with benchling/config_migration/response_objects/importer/perform_config_migration_response.py#PerformConfigMigrationSuccessResponseJson
export interface ImportSuccessResponse {
  status: MigrationStatus.SUCCESS;
  changelog: Array<MigrationObjectChange>;
  changelogUrl: string | null;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  unattributedErrorsBySource: {[unattributedErrorSource: string]: Array<MigrationError>};
}

// Keep in sync with benchling/config_migration/response_objects/importer/perform_config_migration_response.py#PerformConfigMigrationFailureResponseJson
export interface ImportErrorResponse {
  status: MigrationStatus.FAILURE;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  unattributedErrorsBySource: {[unattributedErrorSource: string]: Array<MigrationError>};
}

function validateConfigMigration(
  organizationId: string,
  importUrl: string,
  actionsByObjectId: ActionsByObjectId
): Promise<ImportSuccessResponse | ImportErrorResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:validate',
      data: {
        organizationId,
        importUrl,
        actionsByObjectId: mapObject(actionsByObjectId, transformObjectActionToServerRepresentation),
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

function importConfigMigration(
  organizationId: string,
  importUrl: string,
  importName: string | undefined,
  importDescription: string | undefined,
  actionsByObjectId: ActionsByObjectId
): Promise<ImportSuccessResponse | ImportErrorResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:import',
      data: {
        organizationId,
        importUrl,
        importName,
        importDescription,
        actionsByObjectId: mapObject(actionsByObjectId, transformObjectActionToServerRepresentation),
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

function uploadMigrationFile(file: File): JQueryPromise<string> {
  return ImportUtil.upload(file);
}

function getConfigMigrationURL(): string {
  return '/config-migration';
}
